/**
 * OTSmartInbox · /ot-smart
 * v2.2.0 — Rediseño con aspecto idéntico a OT Manuales (tabla oscura, badges,
 * mismos filtros y layout). Muestra TODOS los campos del mapping de Odoo
 * (código AFC, cliente, dirección, sucursal, suscripción, plan, técnico, GPS).
 */
import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import Pagination from "../components/Pagination";
import OTActionMenu from "../components/OTActionMenu";
import CalendarView from "../components/CalendarView";
import MapSplitView from "../components/MapSplitView";
import TechTimeline from "../components/TechTimeline";
import SyncHealthPanel from "../components/SyncHealthPanel";
import OdooDesfasePanel from "../components/OdooDesfasePanel";
import {
  Search, Loader2, MapPin, Wifi, WifiOff, ChevronRight, ChevronLeft, ChevronDown, Zap, RefreshCw,
  Radar, Package, AlertCircle, LayoutGrid, List, Calendar as CalIcon, User, Download,
  CloudDownload, Clock, CheckCircle2, AlertTriangle, XCircle, Inbox, Server, UserPlus, ClipboardCheck,
  Cpu, Router, FileText, CalendarClock, MoreHorizontal,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { useGlobalFilters } from "../lib/globalFilters";
import { getActiveBranchId } from "../components/BranchSelector";
import GlobalFilterBar from "../components/GlobalFilterBar";

const STATUS_LABELS = {
  pending: "Pendiente",
  assigned: "Asignada",
  in_progress: "En progreso",
  on_site: "En sitio",
  completed: "Completada",
  cancelled: "Cancelada",
};

const STATUS_COLORS = {
  pending: "bg-slate-100 text-slate-700",
  assigned: "bg-blue-100 text-blue-800",
  in_progress: "bg-amber-100 text-amber-800",
  on_site: "bg-cyan-100 text-cyan-800",
  completed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-red-100 text-red-800",
};

// v101.45.1 · Qué mostrar en la fila principal de cada tarjeta Kanban (persistente)
const KANBAN_CARD_LABEL_KEY = "afc:ot-smart-kanban-label";
const KANBAN_LABEL_MODES = { client: "client", task: "task", both: "both" };

function readKanbanLabelMode() {
  try {
    const v = localStorage.getItem(KANBAN_CARD_LABEL_KEY);
    if (v === KANBAN_LABEL_MODES.task || v === KANBAN_LABEL_MODES.both || v === KANBAN_LABEL_MODES.client) return v;
  } catch (_) { /* ignore */ }
  return KANBAN_LABEL_MODES.client;
}

function kanbanOtClient(it) {
  return it.client_name || it.partner_name || "\u2014";
}

function kanbanOtTask(it) {
  const t = (it.task_name || "").trim();
  return t || kanbanOtClient(it);
}

/** Fila 2 del card: una sola línea de altura; modo global Cliente | Título | Ambos */
function KanbanCardTitleRow({ it, mode }) {
  const client = kanbanOtClient(it);
  const task = (it.task_name || "").trim();
  const fullTitle = task ? `${task} · ${client}` : client;

  if (mode === KANBAN_LABEL_MODES.task) {
    return (
      <div className="text-[10px] font-semibold text-gray-800 truncate" title={fullTitle} data-testid="kanban-card-label">
        {kanbanOtTask(it)}
      </div>
    );
  }
  if (mode === KANBAN_LABEL_MODES.both) {
    return (
      <div className="flex items-center gap-1 min-w-0 text-[10px]" title={fullTitle} data-testid="kanban-card-label">
        <span className="font-semibold text-gray-800 truncate min-w-0 flex-1">{kanbanOtTask(it)}</span>
        {task && (
          <>
            <span className="text-gray-300 flex-shrink-0 select-none" aria-hidden>·</span>
            <span className="text-[9px] font-medium text-gray-500 truncate flex-shrink-0 max-w-[96px]">{client}</span>
          </>
        )}
      </div>
    );
  }
  return (
    <div className="text-[10px] font-semibold text-gray-800 truncate" title={fullTitle} data-testid="kanban-card-label">
      {client}
    </div>
  );
}

export default function OTSmartInbox() {
  const navigate = useNavigate();
  const { hasPerm, user: authUser } = useAuth();
  const { asParams: gParams, options: gOptions } = useGlobalFilters();
  const canExport = hasPerm("M1.export");
  const [exporting, setExporting] = useState(false);
  // v101.225.0 · Menú «Más» de la cabecera (acciones secundarias)
  const [masOpen, setMasOpen] = useState(false);
  const masRef = useRef(null);
  useEffect(() => {
    if (!masOpen) return undefined;
    const fuera = (e) => { if (masRef.current && !masRef.current.contains(e.target)) setMasOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setMasOpen(false); };
    document.addEventListener("mousedown", fuera); document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", fuera); document.removeEventListener("keydown", esc); };
  }, [masOpen]);
  // v101.224.0 · Exportar OTs (CSV o Excel con formato profesional) con los filtros activos
  const fetchAllForExport = async () => {
    const BATCH = 200; let all = []; let offset = 0;
    const baseParams = { ...gParams, limit: BATCH, fresh: true };
    if (branchFilter) baseParams.branch_id = branchFilter;
    while (offset < 20000) {
      const { data: resp } = await api.get("/v1/ot_smart/inbox", { params: { ...baseParams, offset } });
      const items = resp?.items || resp?.data || [];
      all = all.concat(items);
      if (items.length < BATCH) break;
      offset += BATCH;
    }
    return all;
  };
  const filtrosTexto = () => {
    const p = gParams || {}; const out = [];
    const nombres = (ids, lista) => String(ids).split(",").map((id) => ((lista || []).find((x) => String(x.id) === id) || {}).name || id).join(", ");
    if (p.branch_office_id || branchFilter) out.push(`Sucursal: ${nombres(p.branch_office_id || branchFilter, gOptions?.branches)}`);
    if (p.zone_id) out.push(`Zonas: ${String(p.zone_id).split(",").length}`);
    if (p.technician_id) out.push(`Técnico: ${nombres(p.technician_id, gOptions?.technicians)}`);
    if (p.internal_status) out.push(`Estado: ${p.internal_status}`);
    if (p.odoo_stage_name) out.push(`Etapa Odoo: ${p.odoo_stage_name}`);
    if (p.q) out.push(`Búsqueda: «${p.q}»`);
    if (p.cpe) out.push(`CPE: ${p.cpe}`);
    if (p.created_from || p.created_to) out.push(`Creadas: ${p.created_from || "…"} a ${p.created_to || "…"}`);
    if (p.scheduled_from || p.scheduled_to) out.push(`Agendadas: ${p.scheduled_from || "…"} a ${p.scheduled_to || "…"}`);
    if (p.has_gps === "true" || p.has_gps === "false") out.push(p.has_gps === "true" ? "Con GPS" : "Sin GPS");
    if (p.stage_mismatch) out.push("Desfase de etapa");
    if (p.no_activity) out.push("Sin actividad");
    return out.join(" · ");
  };
  const exportExcel = async () => {
    setExporting(true);
    try {
      const all = await fetchAllForExport();
      if (!all.length) { toast.error("No hay OTs para exportar"); return; }
      const resp = await api.post("/v1/ot_smart/export-xlsx", { items: all, filtros: filtrosTexto() },
        { responseType: "blob", timeout: 300000 });
      const url = URL.createObjectURL(resp.data); const a = document.createElement("a"); a.href = url;
      a.download = `OTs_Alfanet_${new Date().toISOString().slice(0,16).replace(/[:T]/g,"-")}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success(`${all.length} OTs exportadas a Excel`);
    } catch (e) {
      let msg = "Error al exportar a Excel";
      try { const t = await e?.response?.data?.text?.(); if (t) msg = JSON.parse(t).detail || msg; } catch { /* sin detalle */ }
      toast.error(msg);
    } finally { setExporting(false); }
  };
  // v101.181.0 · Desfase con Odoo (cerradas/canceladas aquí que siguen abiertas en Odoo)
  const canDesfase = ["super_admin", "admin", "admin_zone", "supervisor"].includes(authUser?.role);
  const [desfaseOpen, setDesfaseOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  // v9.3.2 · Track si es la primera carga para mostrar skeleton solo entonces
  const hasLoadedOnce = useRef(false);
  const [data, setData] = useState({ items: [], total: 0 });
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [branches, setBranches] = useState([]);
  const [view, setView] = useState("kanban"); // list | kanban | calendar
  const [kanbanCardLabel, setKanbanCardLabel] = useState(readKanbanLabelMode);
  const setKanbanCardLabelPersist = (mode) => {
    setKanbanCardLabel(mode);
    try { localStorage.setItem(KANBAN_CARD_LABEL_KEY, mode); } catch (_) { /* ignore */ }
  };
  const [selected, setSelected] = useState(() => new Set()); // ids seleccionados en lista

  // v9.3 · Única fuente de verdad: BranchSelector del TopBar (mismo que Kanban)
  const [branchFilter, setBranchFilter] = useState(() => getActiveBranchId() || "");
  useEffect(() => {
    const onBranchChange = (e) => { setBranchFilter(e.detail?.branch_id || ""); setPage(1); };
    window.addEventListener("afc:branch-change", onBranchChange);
    return () => window.removeEventListener("afc:branch-change", onBranchChange);
  }, []);

  // Kanban server-side: cada columna tiene su propia paginación (5 items por página)
  const KANBAN_PAGE_SIZE = 10;
  const SMART_STATUSES = ["pending", "manual_assignment_required", "assigned", "in_progress", "on_site", "completed", "cancelled"];
  // v3.3.1 · Arquitectura Unificada · 5 carriles alimentados desde master_ot.
  // Ya no hay toggle "Estado Interno" vs "Stage Odoo".
  const [kanbanLanes, setKanbanLanes] = useState([]); // [{key, name, color, bg, count}]
  // { [laneKey]: { items, total, page, loading } }
  const [kanbanLaneCols, setKanbanLaneCols] = useState({});
  // v101.16.0 · Refs para refresco silencioso sin perder filtros/página
  const colsRef = useRef({});
  colsRef.current = kanbanLaneCols;
  const laneSeqRef = useRef({});
  const filterGenRef = useRef(0);
  // Legacy (mantenido para vistas Lista/Calendario/Mapa)
  const [kanbanCols, setKanbanCols] = useState(() =>
    Object.fromEntries(SMART_STATUSES.map((s) => [s, { items: [], total: 0, page: 0, loading: false }]))
  );


  const fetchData = async (opts = {}) => {
    const { silent = false } = opts;
    // En vista Kanban ya no hay fetch global — cada columna se pagina server-side.
    if (view !== "list" && view !== "calendar" && view !== "map") return;
    // v9.3.2 · Solo mostrar skeleton en carga inicial (items vacíos y primera vez)
    if (!silent && !hasLoadedOnce.current) setLoading(true);
    try {
      // list: pageSize; calendar/map: 200 (backend max)
      const isListView = view === "list";
      const bulkLimit = isListView ? pageSize : 200;
      const params = { ...gParams, limit: bulkLimit, offset: isListView ? (page - 1) * pageSize : 0 };
      if (!silent) params.fresh = true;  // v101.40.0 · acciones del usuario: sin caché del servidor
      if (branchFilter) params.branch_id = branchFilter;
      const { data: d } = await api.get("/v1/ot_smart/inbox", { params });
      setData({ items: d.items || [], total: d.total || 0 });
      hasLoadedOnce.current = true;
    } catch (e) {
      // v2.3 · Manejo robusto de detail Pydantic (array de {loc, msg})
      const detail = e?.response?.data?.detail;
      let msg = "Error cargando bandeja";
      if (Array.isArray(detail)) {
        msg = detail.map((x) => `${(x.loc || []).slice(-1)[0] || "campo"}: ${x.msg}`).join(" · ");
      } else if (typeof detail === "string") {
        msg = detail;
      }
      if (!silent) toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Fetch de una única columna del Kanban (server-side paging)
  const fetchKanbanColumn = async (statusKey, pageIdx = 0) => {
    setKanbanCols((prev) => ({ ...prev, [statusKey]: { ...prev[statusKey], loading: true } }));
    try {
      const params = {
        ...gParams,
        limit: KANBAN_PAGE_SIZE,
        offset: pageIdx * KANBAN_PAGE_SIZE,
        internal_status: statusKey,
        fresh: true,  // v101.40.0
      };
      if (branchFilter) params.branch_id = branchFilter;
      const { data: d } = await api.get("/v1/ot_smart/inbox", { params });
      setKanbanCols((prev) => ({
        ...prev,
        [statusKey]: { items: d.items || [], total: d.total || 0, page: pageIdx, loading: false },
      }));
    } catch (e) {
      setKanbanCols((prev) => ({ ...prev, [statusKey]: { ...prev[statusKey], loading: false } }));
    }
  };

  // Refetch de todas las columnas del Kanban en paralelo (v3.3.1 · 5 carriles unificados)
  const fetchAllKanbanCols = () => {
    fetchKanbanLanesAll();
  };

  // v3.3.1 · Fetch de una columna del carril unificado
  // v9.4.0 · Todos los filtros vienen de gParams (filtros globales)
  const fetchKanbanLaneColumn = async (laneKey, pageIdx = 0, totalHint = null, opts = {}) => {
    // v101.16.0 · Guardas anti-respuestas viejas: si cambió el filtro o hubo otra petición más nueva
    //             para este carril, la respuesta atrasada se descarta (antes pisaba el tablero filtrado).
    const seq = (laneSeqRef.current[laneKey] || 0) + 1;
    laneSeqRef.current[laneKey] = seq;
    const gen = filterGenRef.current;
    if (!opts.silent) {
      setKanbanLaneCols((prev) => ({ ...prev, [laneKey]: { ...(prev[laneKey] || {}), loading: true } }));
    }
    try {
      const params = {
        ...gParams,
        limit: KANBAN_PAGE_SIZE,
        offset: pageIdx * KANBAN_PAGE_SIZE,
        lane: laneKey,
        skip_count: true,
      };
      // v101.40.0 · Refresco automático → caché compartida del servidor (10 s); acción del usuario → datos frescos
      if (!opts.silent) params.fresh = true;
      if (branchFilter) params.branch_id = branchFilter;
      const { data: d } = await api.get("/v1/ot_smart/inbox", { params });
      if (laneSeqRef.current[laneKey] !== seq || filterGenRef.current !== gen) return;
      setKanbanLaneCols((prev) => ({
        ...prev,
        [laneKey]: {
          items: d.items || [],
          total: totalHint != null ? totalHint : (prev[laneKey]?.total || 0),
          page: pageIdx,
          loading: false,
        },
      }));
    } catch (e) {
      if (laneSeqRef.current[laneKey] !== seq) return;
      setKanbanLaneCols((prev) => ({ ...prev, [laneKey]: { ...(prev[laneKey] || {}), loading: false } }));
    }
  };

  // v3.3.1 · Descubre los 5 carriles + fetch en paralelo
  // v9.4.0 · Filtros globales únicos
  // v101.16.0 · opts.silent = refresco automático: conserva página de cada carril, sin spinners,
  //             y si falla NO vacía el tablero (antes un error dejaba "Cargando carriles…").
  const fetchKanbanLanesAll = async (opts = {}) => {
    const silent = !!opts.silent;
    const gen = filterGenRef.current;
    try {
      // v101.34.0 · El refresco automático usa los conteos compartidos del servidor (caché 20 s para todos
      //             los usuarios). Solo la carga manual fuerza recalcular (antes cada pestaña recalculaba todo).
      const params = silent ? { ...gParams } : { ...gParams, force_refresh: true };
      if (branchFilter) params.branch_id = branchFilter;
      const { data } = await api.get("/v1/ot_smart/kanban-lanes", { params });
      if (filterGenRef.current !== gen) return;  // cambió el filtro mientras tanto
      const lanes = data.lanes || [];
      setKanbanLanes(lanes);
      await Promise.all(
        lanes.map((l) => {
          let pg = 0;
          if (silent) {
            const cur = colsRef.current[l.key]?.page || 0;
            const maxPg = Math.max(0, Math.ceil((l.count || 0) / KANBAN_PAGE_SIZE) - 1);
            pg = Math.min(cur, maxPg);
          }
          return fetchKanbanLaneColumn(l.key, pg, l.count, { silent });
        })
      );
    } catch (e) {
      /* v101.16.0 · conservar lo que ya se ve */
    }
  };

  /* v8.1 · Soft-refresh: solo actualiza conteos SIN borrar items/scroll */
  const refreshKanbanCountsOnly = async () => {
    try {
      const params = { ...gParams };
      if (branchFilter) params.branch_id = branchFilter;
      const { data } = await api.get("/v1/ot_smart/kanban-lanes", { params });
      const lanes = data.lanes || [];
      setKanbanLanes((prev) => {
        // Mantener el orden del backend, solo parchear counts
        return lanes.map((nl) => {
          const old = prev.find((p) => p.key === nl.key);
          return old ? { ...old, count: nl.count } : nl;
        });
      });
    } catch (_) { /* silencioso */ }
  };


  // v9.4.0 · fetchStats pasa TODOS los filtros globales para sincronización exacta
  const fetchStats = async () => {
    try {
      const params = { ...gParams };
      if (branchFilter) params.branch_id = branchFilter;
      const { data } = await api.get("/v1/ot_smart/stats", { params });
      setStats(data);
      setBranches(data.by_branch || []);
    } catch (e) {
      /* silent */
    }
  };

  // v101.16.0 · Generación de filtros: toda respuesta pedida con filtros anteriores se descarta
  useEffect(() => { filterGenRef.current += 1; }, [branchFilter, gParams, view]);
  useEffect(() => { fetchStats(); /* eslint-disable-next-line */ }, [branchFilter, gParams]);
  // v9.4.0 · Refetch al cambiar filtros globales
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [page, pageSize, view, branchFilter, gParams]);
  // v9.4.0 · Kanban: refetch al cambiar branchFilter O filtros globales
  useEffect(() => {
    if (view !== "kanban") return;
    fetchKanbanLanesAll();
    /* eslint-disable-next-line */
  }, [view, branchFilter, gParams]);

  // v9.4.0 · Reset page y refetch cuando cambian filtros globales
  useEffect(() => {
    setPage(1);
    /* eslint-disable-next-line */
  }, [gParams]);
  // Limpiar selección múltiple si cambian filtros, página o vista
  useEffect(() => { setSelected(new Set()); }, [page, pageSize, view, gParams]);

  // v97.4.0 · Auto-refresh de carriles cuando una OT cambia (p. ej. el técnico la ejecuta en la app).
  //           Consulta una huella liviana cada 15 s y recarga solo si cambió.
  const viewRef = useRef(view);
  viewRef.current = view;
  // v101.16.0 · Siempre llamar a la versión ACTUAL de las funciones (con los filtros vigentes).
  //             Antes el refresco del A2 usaba una copia vieja → recargaba sin filtros y "borraba" OTs.
  const latestRef = useRef({});
  latestRef.current = { fetchKanbanLanesAll, fetchData, fetchStats };
  useEffect(() => {
    let last = null;
    let cancelled = false;
    let busy = false;
    const tick = async () => {
      if (cancelled || busy || document.hidden) return;
      busy = true;
      try {
        const { data } = await api.get("/v1/ot_smart/changes");
        const tok = data?.token || "";
        if (last !== null && tok && tok !== last) {
          // v101.16.0 · Refresco silencioso: mantiene filtros, página de cada carril y scroll
          if (viewRef.current === "kanban") await latestRef.current.fetchKanbanLanesAll({ silent: true });
          else await latestRef.current.fetchData({ silent: true });
          latestRef.current.fetchStats();
        }
        last = tok;
      } catch (_) { /* silencioso */ }
      finally { busy = false; }
    };
    tick();
    const iv = setInterval(tick, 30000);  // v101.16.0 · 30 s (antes 15 s)
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { cancelled = true; clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
    /* eslint-disable-next-line */
  }, [view, branchFilter, gParams]);

  // v2.8.9.5 · Auto-refresh cuando el A2 Autopilot termina un ciclo
  useEffect(() => {
    let lastRunAt = null;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get("/a2/autopilot/status");
        const cur = data?.last_run_at || null;
        if (lastRunAt && cur && cur !== lastRunAt) {
          // v101.5.0 · Tras A2 hay que recargar TARJETAS + conteos (no solo badges).
          // Antes refreshKanbanCountsOnly → "1 NUEVAS" en el header y columna "Sin OTs".
          // v101.16.0 · versión actual (filtros vigentes) y silenciosa
          if (viewRef.current === "kanban") {
            await latestRef.current.fetchKanbanLanesAll({ silent: true });
          } else {
            await latestRef.current.fetchData({ silent: true });
          }
          latestRef.current.fetchStats();
          const applied = data?.last_apply_count || 0;
          const manual = data?.last_manual_count || 0;
          if (applied || manual) {
            toast.success(`A2 actualizó el tablero · ${applied} auto-agendadas${manual ? ` · ${manual} rescate` : ""}`, { duration: 4000 });
          }
        }
        lastRunAt = cur;
      } catch (_) { /* silencioso */ }
    };
    poll();
    const iv = setInterval(() => { if (!cancelled) poll(); }, 20000);
    return () => { cancelled = true; clearInterval(iv); };
    /* eslint-disable-next-line */
  }, [view]);

  // v2.3 · Mapeo de OT SMART → shape esperado por CalendarView — solo OTs asignadas con técnico y fecha
  const calendarOrders = useMemo(() =>
    (data.items || [])
      .filter((r) => r.scheduled_at && r.technician_id && r.internal_status === "assigned")
      .map((r) => ({
        id: r.odoo_task_id,
        code: r.afc_code,
        status: mapSmartStatusToCalStatus(r.internal_status),
        scheduled_at: r.scheduled_at,
        priority: (r.internal_priority || "").toUpperCase().replace(/^P/i, "P") || "P3",
        technician_name: r.technician_name,
        client_name: r.partner_name,
        solution_type_name: r.plan_name || r.plan_type || r.task_name,
      })),
    [data.items]
  );

  const totalTasks = stats?.total_tasks || 0;
  const withGps = stats?.with_gps || 0;
  const gpsPercent = totalTasks > 0 ? Math.round((withGps / totalTasks) * 100) : 0;

  return (
    <Layout>
      <div className="space-y-3" data-testid="ot-smart-inbox">
        {/* ═══ Header · v101.225.0 · ordenado: título + datos | acciones principales; abajo vistas ═══ */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <h1 className="text-xl font-bold tracking-tight flex items-center gap-2" style={{ color: "#0A3747" }}>
                <Zap size={20} className="text-[#E0E621]" fill="#E0E621" strokeWidth={1.5} />
                OT SMART
              </h1>
              <div className="hidden md:flex items-center gap-2 text-[10px] font-semibold" style={{ color: "#64748B" }}>
                <span className="px-2 py-0.5 rounded-full" style={{ background: "#F1F5F9" }}>{totalTasks} OTs</span>
                <span className="px-2 py-0.5 rounded-full" style={{ background: "#ECFDF5", color: "#059669" }}>{withGps} GPS ({gpsPercent}%)</span>
                {stats?.last_materialized_at && (
                  <span className="px-2 py-0.5 rounded-full" style={{ background: "#FBFCE0", color: "#6B7000" }} title={`Master: ${stats.last_materialized_at}`}>
                    Sync: {new Date(stats.last_materialized_at).toLocaleTimeString("es-EC", {timeZone: "America/Guayaquil", hour: "2-digit", minute: "2-digit", hour12: false})}
                  </span>
                )}
              </div>
            </div>
            {/* Acciones principales + menú «Más» */}
            <div className="flex items-center gap-1.5" data-testid="ot-header-actions">
              <button onClick={async () => { try { await api.post("/admin/odoo-live/poll-orders?max_batches=5"); toast.success("Sync Odoo iniciado"); setTimeout(() => { fetchData(); fetchStats(); }, 2500); } catch (e) { toast.error(e?.response?.data?.detail || "Error sync"); } }} data-testid="btn-sync-odoo-now" className="neu-btn neu-btn-primary" style={{ padding: "6px 12px", fontSize: 11 }}>
                <CloudDownload size={12} /> Sync Odoo
              </button>
              <button onClick={() => { fetchData(); fetchStats(); }} disabled={loading} data-testid="btn-refresh" className="neu-btn" style={{ padding: "6px 10px", fontSize: 11 }} title="Refrescar">
                {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Refrescar
              </button>
              {canExport && (
                <button onClick={exportExcel} disabled={exporting} data-testid="btn-export-orders-xlsx"
                  className="neu-btn neu-btn-accent" style={{ padding: "6px 12px", fontSize: 11 }}
                  title="Excel con formato: encabezado, filtros, fechas, estados con color y hoja de resumen">
                  {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Excel
                </button>
              )}
              <div className="relative" ref={masRef}>
                <button onClick={() => setMasOpen((v) => !v)} data-testid="btn-header-mas" className="neu-btn"
                  style={{ padding: "6px 10px", fontSize: 11 }} aria-haspopup="menu" aria-expanded={masOpen}>
                  <MoreHorizontal size={14} /> Más
                </button>
                {masOpen && (
                  <div role="menu" className="absolute right-0 mt-1 w-56 rounded-xl border border-slate-200 bg-white shadow-lg py-1 z-50">
                    <button role="menuitem" data-testid="btn-materialize" className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
                      onClick={async () => { setMasOpen(false); try { await api.post("/v1/ot_smart/materialize"); toast.success("Rematerializada"); fetchData(); fetchStats(); } catch { toast.error("Error rematerializando"); } }}>
                      <Package size={13} /> Rematerializar
                    </button>
                    {canDesfase && (
                      <button role="menuitem" data-testid="btn-desfase-odoo" className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
                        title="Cerradas o canceladas aquí que siguen abiertas en Odoo"
                        onClick={() => { setMasOpen(false); setDesfaseOpen(true); }}>
                        <AlertTriangle size={13} /> Desfase con Odoo
                      </button>
                    )}
                    <div className="my-1 border-t border-slate-100" />
                    <button role="menuitem" data-testid="btn-goto-sync" className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
                      onClick={() => { setMasOpen(false); navigate("/odoo"); }}>
                      <Radar size={13} /> Configuración de sincronización
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Vistas y opciones de la vista */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="neu-pill-group" data-testid="view-toggle">
              <button className={`neu-pill ${view === "list" ? "active" : ""}`} onClick={() => setView("list")} data-testid="btn-view-list"><List size={12} /> Lista</button>
              <button className={`neu-pill ${view === "kanban" ? "active" : ""}`} onClick={() => setView("kanban")} data-testid="btn-view-kanban"><LayoutGrid size={12} /> Pipeline</button>
              <button className={`neu-pill ${view === "calendar" ? "active" : ""}`} onClick={() => setView("calendar")} data-testid="btn-view-calendar"><CalIcon size={12} /> Calendario</button>
              <button className={`neu-pill ${view === "map" ? "active" : ""}`} onClick={() => setView("map")} data-testid="btn-view-mapa"><MapPin size={12} /> Mapa</button>
            </div>
        {view === "kanban" && (
          <div className="neu-pill-group" data-testid="kanban-label-toggle" title="Qué mostrar en cada tarjeta del Pipeline">
            <span className="hidden sm:inline text-[9px] font-bold uppercase tracking-wide text-slate-500 px-1 self-center">Tarjeta</span>
            <button
              type="button"
              className={`neu-pill ${kanbanCardLabel === KANBAN_LABEL_MODES.client ? "active" : ""}`}
              onClick={() => setKanbanCardLabelPersist(KANBAN_LABEL_MODES.client)}
              data-testid="btn-kanban-label-client"
            >
              <User size={12} /> Cliente
            </button>
            <button
              type="button"
              className={`neu-pill ${kanbanCardLabel === KANBAN_LABEL_MODES.task ? "active" : ""}`}
              onClick={() => setKanbanCardLabelPersist(KANBAN_LABEL_MODES.task)}
              data-testid="btn-kanban-label-task"
            >
              <FileText size={12} /> Título
            </button>
            <button
              type="button"
              className={`neu-pill ${kanbanCardLabel === KANBAN_LABEL_MODES.both ? "active" : ""}`}
              onClick={() => setKanbanCardLabelPersist(KANBAN_LABEL_MODES.both)}
              data-testid="btn-kanban-label-both"
            >
              Ambos
            </button>
          </div>
        )}
          </div>
        </div>

        {canDesfase && <OdooDesfasePanel open={desfaseOpen} onOpenChange={setDesfaseOpen} />}

        {/* ═══ Barra de filtros global (persistente localStorage) ═══ */}
        <GlobalFilterBar />

        {/* Tabla / Kanban / Calendario */}
        {/* ═══ v8.1 · Resumen de carriles — vista rápida ═══ */}
        {view === "kanban" && kanbanLanes.length > 0 && (
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-thin" data-testid="lane-summary-bar">
            {kanbanLanes.map((lane) => {
              const iconMap = {
                nuevas: Inbox, infraestructura: Server, revision: AlertTriangle,
                asignadas: UserPlus, en_ejecucion: Zap, fiscalizacion: ClipboardCheck,
                realizadas: CheckCircle2, canceladas: XCircle,
              };
              const LIcon = iconMap[lane.key] || Package;
              const isZero = lane.count === 0;
              return (
                <div
                  key={lane.key}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl flex-shrink-0 transition-all cursor-default"
                  style={{
                    background: isZero ? "#F8FAFC" : lane.bg || "#F1F5F9",
                    border: `1.5px solid ${isZero ? "#E2E8F0" : lane.color + "30"}`,
                    opacity: isZero ? 0.55 : 1,
                  }}
                  data-testid={`lane-chip-${lane.key}`}
                >
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: lane.color, opacity: isZero ? 0.4 : 0.85 }}
                  >
                    <LIcon size={13} color="#fff" strokeWidth={2.5} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-base font-black leading-none" style={{ color: lane.color }}>
                      {lane.count}
                    </div>
                    <div className="text-[8px] font-semibold uppercase tracking-wider leading-tight truncate max-w-[80px]" style={{ color: isZero ? "#94A3B8" : lane.color }}>
                      {lane.name.split(" ")[0]}
                    </div>
                  </div>
                </div>
              );
            })}
            {/* Total */}
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl flex-shrink-0 ml-1" style={{ background: "#0A3747", border: "1.5px solid #0A3747" }}>
              <div className="text-base font-black text-[#E0E621] leading-none">{kanbanLanes.reduce((s, l) => s + l.count, 0)}</div>
              <div className="text-[8px] font-semibold uppercase tracking-wider text-white/70 leading-tight">Total</div>
            </div>
          </div>
        )}

        <div className={view === "map" ? "" : "neu-card overflow-hidden"}>
          {view === "map" ? (
            <MapSplitView
              items={data.items}
              loading={loading}
              onRefresh={() => { fetchData(); fetchStats(); }}
            />
          ) : view === "kanban" ? (
            <div data-testid="kanban-wrapper">
              {/* v2.8.7.4 · Toggle: agrupar por estado interno vs stage Odoo */}
              <SmartKanbanLanes
                lanes={kanbanLanes}
                cols={kanbanLaneCols}
                pageSize={KANBAN_PAGE_SIZE}
                cardLabelMode={kanbanCardLabel}
                onOpen={(ot) => navigate(`/ot-smart/${ot.odoo_task_id}`)}
                onColPageChange={(laneKey, newPage) => fetchKanbanLaneColumn(laneKey, newPage, null)}
                onActionDone={() => { fetchKanbanLanesAll({ silent: true }); fetchStats(); }}
              />
              <div className="px-4 pb-4">
                <TechTimeline onSlotClick={({ tech, slot }) => {
                  if (slot?.ot_id) navigate(`/ot-smart/${slot.ot_id}`);
                }} />
              </div>
            </div>
          ) : loading ? (
            <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div>
          ) : data.items.length === 0 ? (
            <EmptyState totalTasks={stats?.total_tasks || 0} />
          ) : view === "calendar" ? (
            <div className="p-3">
              <CalendarView orders={calendarOrders} onOpenOrder={(id) => navigate(`/ot-smart/${id}`)} />
              {calendarOrders.length === 0 && (
                <div className="text-center py-10 text-xs text-gray-500">
                  Ninguna OT tiene fecha programada · asigna un técnico y programa la OT para verla en el calendario.
                </div>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              {selected.size > 0 && (
                <div
                  className="flex items-center justify-between px-4 py-2 bg-[#E0F2F1] border-b border-[#0A3747]/20 text-xs"
                  data-testid="smart-bulk-bar"
                >
                  <span className="font-semibold text-[#0A3747]">
                    {selected.size} OT{selected.size === 1 ? "" : "s"} seleccionada{selected.size === 1 ? "" : "s"}
                  </span>
                  <button
                    onClick={() => setSelected(new Set())}
                    className="px-3 py-1 text-[10px] font-mono uppercase tracking-wider border border-[#0A3747]/40 rounded hover:bg-white"
                    data-testid="smart-bulk-clear"
                  >
                    Deseleccionar todo
                  </button>
                </div>
              )}
              <table className="w-full text-xs" data-testid="ot-smart-table">
                <thead className="bg-[#0A3747] text-white text-left text-[10px] uppercase font-mono tracking-wider">
                  <tr>
                    <th className="px-3 py-2 w-8">
                      {(() => {
                        const visibleIds = data.items.map((r) => r.odoo_task_id);
                        const allSel = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
                        const someSel = visibleIds.some((id) => selected.has(id));
                        return (
                          <input
                            type="checkbox"
                            checked={allSel}
                            ref={(el) => { if (el) el.indeterminate = !allSel && someSel; }}
                            onChange={() => {
                              setSelected((prev) => {
                                const next = new Set(prev);
                                if (allSel) visibleIds.forEach((id) => next.delete(id));
                                else visibleIds.forEach((id) => next.add(id));
                                return next;
                              });
                            }}
                            className="cursor-pointer"
                            data-testid="smart-select-all"
                            aria-label="Seleccionar todas las OT visibles"
                          />
                        );
                      })()}
                    </th>
                    <th className="px-3 py-2 font-semibold">Código AFC</th>
                    <th className="px-3 py-2 font-semibold w-20">Prioridad A6</th>
                    <th className="px-3 py-2 font-semibold">Cliente</th>
                    <th className="px-3 py-2 font-semibold">Sucursal</th>
                    <th className="px-3 py-2 font-semibold">Plan / Tipo</th>
                    <th className="px-3 py-2 font-semibold">Técnico</th>
                    <th className="px-3 py-2 font-semibold">Creada</th>
                    <th className="px-3 py-2 font-semibold">Agendada</th>
                    <th className="px-3 py-2 font-semibold">Integridad</th>
                    <th className="px-3 py-2 font-semibold">Estado</th>
                    <th className="px-3 py-2 font-semibold"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((r) => {
                    const gpsValid = r.partner_lat && r.partner_lon && r.partner_lat !== 0 && r.partner_lon !== 0;
                    const isSelected = selected.has(r.odoo_task_id);
                    const integrity = getIntegrity(r);
                    const hasAlert = !integrity.complete;
                    return (
                      <tr
                        key={r.odoo_task_id}
                        onClick={() => navigate(`/ot-smart/${r.odoo_task_id}`)}
                        data-testid={`smart-row-${r.odoo_task_id}`}
                        className={`border-t border-gray-100 hover:bg-gray-50 cursor-pointer text-xs ${isSelected ? "bg-[#E0F2F1]/40" : ""} ${hasAlert ? "!bg-red-50/40" : ""} ${r.a1_stock_alert === "red" ? "!bg-red-50 border-l-4 !border-l-red-500" : ""}`}
                      >
                        <td className="px-3 py-1.5" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              setSelected((prev) => {
                                const next = new Set(prev);
                                if (next.has(r.odoo_task_id)) next.delete(r.odoo_task_id);
                                else next.add(r.odoo_task_id);
                                return next;
                              });
                            }}
                            className="cursor-pointer"
                            data-testid={`smart-row-select-${r.odoo_task_id}`}
                            aria-label={`Seleccionar ${r.afc_code}`}
                          />
                        </td>
                        <td className="px-3 py-1.5 font-mono text-[11px] font-semibold text-[#0A3747] whitespace-nowrap">
                          {r.afc_code}
                          {r.a1_stock_alert === "red" && (
                            <span className="ml-1 inline-block px-1 rounded text-[8px] font-bold bg-red-500 text-white" title="Stock insuficiente" data-testid={`stock-alert-${r.odoo_task_id}`}>SIN STOCK</span>
                          )}
                          <div className="text-[9px] text-gray-400">
                            {r.task_number || `Odoo #${r.odoo_task_id}`}
                          </div>
                        </td>
                        <td className="px-3 py-1.5 text-center" data-testid={`a6-cell-${r.odoo_task_id}`}>
                          {r.a6_score != null ? (
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold tabular-nums ${
                              Number(r.a6_score) >= 80 ? "bg-red-100 text-red-800" :
                              Number(r.a6_score) >= 60 ? "bg-amber-100 text-amber-800" :
                              "bg-emerald-100 text-emerald-800"}`}
                              title={`Posición #${r.a6_position}${r.a6_locked ? ' · bloqueada manual' : ''}`}>
                              {r.a6_locked && <span title="Manual" className="text-yellow-600">🔒</span>}
                              {Number(r.a6_score).toFixed(1)}
                            </span>
                          ) : <span className="text-gray-300 text-[10px]">—</span>}
                        </td>
                        <td className="px-3 py-1.5 font-medium text-gray-900 whitespace-nowrap max-w-[180px] truncate" title={r.partner_name}>
                          {r.partner_name || "—"}
                          {r.partner_vat && <div className="text-[9px] font-mono text-gray-400">{r.partner_vat}</div>}
                        </td>
                        <td className="px-3 py-1.5 text-gray-600 whitespace-nowrap max-w-[140px] truncate" title={r.branch_name}>
                          {r.branch_name || <span className="italic text-gray-400">—</span>}
                        </td>
                        <td className="px-3 py-1.5 whitespace-nowrap max-w-[160px] truncate" title={r.plan_name || r.solution_type_name}>
                          {r.solution_type_name || r.plan_name ? (
                            <div>
                              <div className="text-gray-800 truncate">{r.solution_type_name || r.plan_name}</div>
                              {r.bandwidth && <div className="text-[9px] font-mono text-gray-500">{r.bandwidth} Mbps</div>}
                            </div>
                          ) : <span className="text-gray-400 italic">Sin tipo</span>}
                        </td>
                        <td className="px-3 py-1.5 text-gray-700 whitespace-nowrap max-w-[140px] truncate" title={r.technician_name}>
                          {r.technician_name ? (
                            <span className="inline-flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                              <span className="truncate">{r.technician_name}</span>
                            </span>
                          ) : (
                            <span className="italic text-red-400">Sin asignar</span>
                          )}
                        </td>
                        {/* v9.3.4 · Fechas en formato Ecuador UTC-5 */}
                        <td className="px-3 py-1.5 whitespace-nowrap text-[10px] text-gray-500 font-mono">
                          {fmtEcDate(r.odoo_created_at) || "—"}
                        </td>
                        <td className="px-3 py-1.5 whitespace-nowrap text-[10px] font-mono">
                          {r.scheduled_at ? (
                            <span className="text-blue-600 font-semibold">{fmtEcDate(r.scheduled_at)}</span>
                          ) : <span className="text-red-400 italic">Sin agendar</span>}
                        </td>
                        {/* v9.3.4 · Indicadores de integridad */}
                        <td className="px-3 py-1.5 whitespace-nowrap" data-testid={`integrity-${r.odoo_task_id}`}>
                          {integrity.complete ? (
                            <span className="inline-flex items-center gap-0.5 text-emerald-600 text-[10px] font-bold">
                              <CheckCircle2 size={12} /> {integrity.ok}/{integrity.total}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1">
                              <span className="text-red-500 text-[10px] font-bold">{integrity.ok}/{integrity.total}</span>
                              <span className="inline-flex gap-0.5">
                                {integrity.missing.map((m) => (
                                  <span key={m.key} className="text-red-500 cursor-help" title={`Falta: ${m.label}`}>
                                    <m.icon size={10} strokeWidth={2.5} />
                                  </span>
                                ))}
                              </span>
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 whitespace-nowrap">
                          {r.effective_stage_name || r.stage_name ? (
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-purple-100 text-purple-800 border border-purple-200"
                              title={`Odoo stage_id ${r.stage_id_odoo || "—"}${r.stage_category ? " · " + r.stage_category : ""}`}
                              data-testid={`stage-${r.odoo_task_id}`}
                            >
                              {r.effective_stage_name || r.stage_name}
                            </span>
                          ) : (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${STATUS_COLORS[r.internal_status] || "bg-gray-100 text-gray-700"}`}>
                              {STATUS_LABELS[r.internal_status] || r.internal_status}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          <ChevronRight size={12} className="text-gray-400" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {data.total > 0 && view === "list" && (
            <Pagination
              page={page}
              pageSize={pageSize}
              total={data.total}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              testIdPrefix="ot-smart-pagination"
            />
          )}
        </div>
      </div>
    </Layout>
  );
}

function StatCard({ label, value, icon: Icon, tone, testid }) {
  const styles = {
    default: { bg: "linear-gradient(145deg, #F8FAFC, #E4EAF0)",         fg: "#0A3747", ring: "rgba(163,177,198,0.55)" },
    yellow:  { bg: "linear-gradient(145deg, #EDF34C, #C4CA12)",         fg: "#3F2E00", ring: "rgba(163,168,33,0.5)"  },
    green:   { bg: "linear-gradient(145deg, #34D399, #10B981)",         fg: "#064E3B", ring: "rgba(16,185,129,0.5)"  },
    amber:   { bg: "linear-gradient(145deg, #FCD34D, #F59E0B)",         fg: "#78350F", ring: "rgba(245,158,11,0.5)"  },
    cyan:    { bg: "linear-gradient(145deg, #93C5FD, #3B82F6)",         fg: "#1E3A8A", ring: "rgba(59,130,246,0.5)"  },
  };
  const st = styles[tone] || styles.default;
  return (
    <div
      className="afc-appear rounded-2xl px-4 py-3 flex items-center justify-between transition-all hover:-translate-y-0.5"
      style={{
        background: st.bg,
        boxShadow: `-6px -6px 14px rgba(255,255,255,0.75), 6px 6px 14px ${st.ring}, inset -1px -1px 3px rgba(0,0,0,0.05), inset 1px 1px 3px rgba(255,255,255,0.5)`,
      }}
      data-testid={testid}
    >
      <div>
        <div className="text-[9px] uppercase tracking-wider font-bold" style={{ color: st.fg, opacity: 0.75 }}>{label}</div>
        <div className="text-2xl font-bold mt-0.5" style={{ color: st.fg, textShadow: "0 1px 0 rgba(255,255,255,0.4)" }}>{value}</div>
      </div>
      <div
        className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
        style={{
          background: "rgba(255,255,255,0.5)",
          boxShadow: "inset -1.5px -1.5px 4px rgba(255,255,255,0.85), inset 1.5px 1.5px 4px rgba(0,0,0,0.08)",
        }}
      >
        <Icon size={20} style={{ color: st.fg }} strokeWidth={2} />
      </div>
    </div>
  );
}

function EmptyState({ totalTasks }) {
  return (
    <div className="p-12 text-center" data-testid="empty-state">
      <Radar className="mx-auto text-gray-300 mb-3" size={48} />
      <div className="text-sm font-semibold text-gray-700 mb-1">
        {totalTasks === 0 ? "Aún no hay OTs sincronizadas desde Odoo" : "No hay resultados con estos filtros"}
      </div>
      <p className="text-xs text-gray-500 max-w-md mx-auto">
        {totalTasks === 0 ? (
          <>Habilita la sincronización de <b>tasks</b>, <b>partners</b>, <b>branches</b>, <b>subscriptions</b>, <b>products</b> y <b>segments</b> en el <a href="/odoo" className="text-cyan-600 hover:underline">panel de Odoo</a>. La ingesta corre cada 5 minutos.</>
        ) : "Prueba a limpiar los filtros o ajustar la búsqueda."}
      </p>
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════════════════
// v3.1.1 · Tarjeta especial REVISIÓN · Buzón de Excepciones (A1/A2/A6)
// ═══════════════════════════════════════════════════════════════════════════
const AGENT_META = {
  A1: { label: "A1 · Prefactibilidad", bg: "#DBEAFE", fg: "#1E40AF", icon: "🧪" },
  A2: { label: "A2 · Agendamiento",    bg: "#FEE2E2", fg: "#991B1B", icon: "📅" },
  A6: { label: "A6 · Priorización",    bg: "#F3E8FF", fg: "#6B21A8", icon: "⚡" },
  MANUAL: { label: "Coordinador",      bg: "#F3F4F6", fg: "#374151", icon: "👤" },
};

function ReviewCard({ it }) {
  const [showForce, setShowForce] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [loadingSugg, setLoadingSugg] = useState(false);
  const [assigning, setAssigning] = useState(null);
  const src = it.review_source || "MANUAL";
  const meta = AGENT_META[src] || AGENT_META.MANUAL;
  const reason = it.review_reason || it.autopilot_fail_reason || "Sin motivo registrado";

  const loadSuggestions = async () => {
    setLoadingSugg(true);
    try {
      const r = await api.get(`/a2/suggest-schedule/${it.odoo_task_id}`);
      setSuggestions(r.data?.suggestions || []);
    } catch (e) {
      toast.error("No se pudieron cargar sugerencias");
    } finally {
      setLoadingSugg(false);
    }
  };

  const assignSuggestion = async (s) => {
    const key = `${s.technician_id}-${s.date}-${s.time_start}`;
    setAssigning(key);
    try {
      await api.post(`/v1/master-ot/${it.odoo_task_id}/force-schedule`, {
        technician_email: s.technician_email,
        scheduled_at: s.exec_start,
      });
      toast.success(`Asignado a ${s.technician_name} el ${s.date} ${s.time_start}`);
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error("Error al asignar: " + (e.response?.data?.detail || e.message));
    } finally {
      setAssigning(null);
    }
  };
  const when = it.review_at ? new Date(it.review_at).toLocaleString("es-EC", { timeZone: "America/Guayaquil", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : null;
  return (
    <>
      <div
        className="mb-2 rounded-lg border-2 bg-white shadow-sm hover:shadow-md transition-all"
        style={{ borderColor: "#DC2626" }}
        data-testid={`review-card-${it.odoo_task_id}`}
      >
        <div className="p-2.5">
          {/* Header con badge del agente que envió */}
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider"
              style={{ background: meta.bg, color: meta.fg }}
              title={`Enviado a revisión por ${meta.label}`}
              data-testid={`review-source-${it.odoo_task_id}`}
            >
              <span>{meta.icon}</span> {src}
            </span>
            <span className="text-[10px] font-mono text-gray-500 truncate">{it.task_number || it.afc_code}</span>
          </div>
          {/* Cliente */}
          <div className="text-[11px] font-semibold text-slate-800 truncate mb-1" title={it.client_name}>
            {it.client_name || "Sin cliente"}
          </div>
          {/* Motivo destacado */}
          <div
            className="text-[10.5px] px-2 py-1.5 rounded font-medium leading-tight mb-1.5"
            style={{ background: "#FEF2F2", color: "#7F1D1D", borderLeft: "3px solid #DC2626" }}
            data-testid={`review-reason-${it.odoo_task_id}`}
          >
            <div className="text-[8.5px] font-bold uppercase tracking-wide opacity-70 mb-0.5">Motivo</div>
            {reason}
          </div>
          {/* Meta pequeña */}
          <div className="flex items-center justify-between text-[9px] text-gray-500">
            <span title={it.activity_type}>{(it.activity_type || "").slice(0, 24)}</span>
            {when && <span title={it.review_at}>{when}</span>}
          </div>
          {/* Acción · Forzar agenda */}
          <button
            onClick={(e) => { e.stopPropagation(); setShowForce(true); }}
            className="mt-2 w-full text-[10px] font-bold py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 flex items-center justify-center gap-1"
            data-testid={`btn-force-schedule-${it.odoo_task_id}`}
          >
            Forzar Agenda
          </button>
          {/* v38.0.0 · Sugerencias de agendamiento IA */}
          {!suggestions && (
            <button
              onClick={(e) => { e.stopPropagation(); loadSuggestions(); }}
              disabled={loadingSugg}
              className="mt-1.5 w-full text-[10px] font-bold py-1 rounded bg-cyan-600 text-white hover:bg-cyan-700 flex items-center justify-center gap-1 disabled:opacity-50"
              data-testid={`btn-suggest-${it.odoo_task_id}`}
            >
              {loadingSugg ? "Calculando..." : "Sugerir Horarios"}
            </button>
          )}
          {suggestions && suggestions.length > 0 && (
            <div className="mt-2 space-y-1.5" data-testid={`suggestions-${it.odoo_task_id}`}>
              <div className="text-[8px] font-bold text-cyan-700 uppercase tracking-wider">Sugerencias IA</div>
              {suggestions.map((s, i) => {
                const key = `${s.technician_id}-${s.date}-${s.time_start}`;
                return (
                  <div key={key} className="border border-cyan-200 rounded-md p-2 bg-cyan-50" data-testid={`suggestion-${i}`}>
                    <div className="text-[11px] font-bold text-slate-800 truncate">{s.technician_name}</div>
                    <div className="text-[10px] text-cyan-800 font-semibold">
                      {s.date} &middot; {s.time_start} - {s.time_end}
                    </div>
                    <div className="flex items-center gap-2 text-[9px] text-gray-500 mt-0.5">
                      {s.distance_km != null && <span>{s.distance_km} km</span>}
                      <span>Carga: {s.carga_actual} OTs</span>
                      <span>Score: {s.ranking_score}</span>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); assignSuggestion(s); }}
                      disabled={assigning === key}
                      className="mt-1 w-full text-[10px] font-bold py-1 rounded bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50"
                      data-testid={`btn-assign-suggestion-${i}`}
                    >
                      {assigning === key ? "Asignando..." : "Asignar"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {suggestions && suggestions.length === 0 && (
            <div className="mt-2 text-[10px] text-amber-600 font-medium bg-amber-50 rounded p-1.5 text-center">
              Sin opciones disponibles en el horizonte configurado
            </div>
          )}
        </div>
      </div>
      {showForce && (
        <ForceScheduleModal
          ot={it}
          onClose={() => setShowForce(false)}
          onSuccess={() => { setShowForce(false); window.location.reload(); }}
        />
      )}
    </>
  );
}

function ForceScheduleModal({ ot, onClose, onSuccess }) {
  const [techs, setTechs] = useState([]);
  const [techEmail, setTechEmail] = useState("");
  const [scheduledAt, setScheduledAt] = useState(() => {
    const d = new Date(); d.setHours(9, 0, 0, 0); d.setDate(d.getDate() + 1);
    // yyyy-MM-ddTHH:mm en local
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/v1/users/technicians", { params: { active: true, limit: 500 } })
      .then((r) => setTechs(r.data?.items || r.data || []))
      .catch(() => {});
  }, []);
  const doForce = async () => {
    if (!techEmail) return toast.error("Elige un técnico");
    setSaving(true);
    try {
      await api.post(`/v1/master-ot/${ot.odoo_task_id}/force-schedule`, {
        technician_email: techEmail,
        scheduled_at: new Date(scheduledAt).toISOString(),
      });
      toast.success("OT forzada a Asignada");
      onSuccess();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al forzar");
    } finally { setSaving(false); }
  };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      data-testid="force-schedule-modal"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <div className="text-[13px] font-bold" style={{ color: "#0A3747" }}>Forzar Asignación</div>
            <div className="text-[10px] text-gray-500">{ot.task_number} · {ot.client_name}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">✕</button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-[11px] font-bold text-gray-700 mb-1 block">Técnico</label>
            <select
              value={techEmail}
              onChange={(e) => setTechEmail(e.target.value)}
              className="w-full text-xs border rounded px-2 py-1.5"
              data-testid="force-tech-select"
            >
              <option value="">— elige un técnico —</option>
              {techs.map((t) => (
                <option key={t.email || t.id} value={t.email}>
                  {t.full_name || t.name || t.email} · {t.branch_code || ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-bold text-gray-700 mb-1 block">Fecha y hora</label>
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              className="w-full text-xs border rounded px-2 py-1.5"
              data-testid="force-datetime"
            />
          </div>
          <div className="pt-2 flex gap-2 justify-end">
            <button onClick={onClose} className="px-3 py-1.5 text-[11px] border rounded hover:bg-gray-50">Cancelar</button>
            <button
              onClick={doForce}
              disabled={saving || !techEmail}
              className="px-3 py-1.5 text-[11px] bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
              data-testid="force-schedule-confirm"
            >
              {saving ? <Loader2 size={11} className="animate-spin"/> : "✅"} Forzar Asignación
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// v9.0 · Kanban Tradicional por Columnas · Reemplaza zigzag/culebra
// ── v9.3.4 · Indicadores de integridad para tarjetas ──
const INTEGRITY_CHECKS = [
  { key: "tech",    label: "Técnico",       icon: User,          check: (it) => !!it.technician_id },
  { key: "cpe",     label: "CPE",           icon: Cpu,           check: (it) => !!it.pe_cpe_serial },
  { key: "olt",     label: "OLT",           icon: Router,        check: (it) => !!it.pe_olt },
  { key: "solucion",label: "Tipo Solución", icon: FileText,      check: (it) => !!(it.solution_type_name || it.plan_name) },
  { key: "agenda",  label: "Agendada",      icon: CalendarClock, check: (it) => !!it.scheduled_at },
  { key: "gps",     label: "GPS",           icon: MapPin,        check: (it) => !!(it.partner_lat && it.partner_lon) },
];

function getIntegrity(it) {
  const missing = [];
  let ok = 0;
  for (const c of INTEGRITY_CHECKS) {
    if (c.check(it)) { ok++; } else { missing.push(c); }
  }
  return { ok, total: INTEGRITY_CHECKS.length, missing, complete: missing.length === 0 };
}

function fmtEcDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-EC", { timeZone: "America/Guayaquil", day: "2-digit", month: "short" }) +
      " " + d.toLocaleTimeString("es-EC", { timeZone: "America/Guayaquil", hour: "2-digit", minute: "2-digit", hour12: false });
  } catch { return null; }
}

// v101.15.0 · Días desde creación Odoo (Kanban, al lado del número de OT)
function daysSinceCreated(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86400000));
}

function ageBadgeStyle(days) {
  // 0–1 verde · 2–3 ámbar · 4–6 naranja · 7+ rojo
  if (days <= 1) return { bg: "#D1FAE5", fg: "#065F46", label: days === 0 ? "Hoy" : "1d" };
  if (days <= 3) return { bg: "#FEF3C7", fg: "#92400E", label: `${days}d` };
  if (days <= 6) return { bg: "#FFEDD5", fg: "#9A3412", label: `${days}d` };
  return { bg: "#FEE2E2", fg: "#991B1B", label: `${days}d` };
}

function IntegrityIcons({ integrity }) {
  if (integrity.complete) {
    return (
      <span className="inline-flex items-center gap-0.5 text-emerald-500" title="Datos completos">
        <CheckCircle2 size={10} strokeWidth={2.5} />
        <span className="text-[8px] font-bold">OK</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5">
      {integrity.missing.map((m) => (
        <span
          key={m.key}
          className="text-red-500 cursor-help"
          title={`Falta: ${m.label}`}
        >
          <m.icon size={9} strokeWidth={2.5} />
        </span>
      ))}
    </span>
  );
}

/** v101.46.1 · Badge sync Odoo (cierre / aprovisionamiento) para Realizadas y Fiscalización */
function getOdooSyncState(it) {
  // v101.162.0 · Cancelación y etapa de Odoo primero (mismas reglas que Integraciones › Sincronización con Odoo)
  const _st = String(it?.internal_status_raw || it?.internal_status || "").toLowerCase();
  const _odooCancel = [20, 21].includes(Number(it?.odoo_stage_id || it?.stage_id_odoo || 0))
    || String(it?.odoo_stage_name || it?.stage_name || "").toLowerCase().includes("cancel");
  const _cst = String(it?.cancel_event_status || "").toLowerCase();
  const _cmsg = (it?.cancel_event_message || "").trim();
  if (["cancelada", "cancelled"].includes(_st)) {
    if (_odooCancel || _cst === "completado") return { kind: "ok", label: "Odoo ✓", title: "Cancelada también en Odoo", pill: true };
    if (_cst.startsWith("error")) return { kind: "error", label: "Odoo ✗", title: _cmsg || "Odoo rechazó la cancelación", pill: true };
    if (_cst === "pendiente_sync") return { kind: "pending", label: "Odoo…", title: _cmsg || "Cancelación pendiente de enviar a Odoo", pill: true };
    return { kind: "warn", label: "Sin Odoo", title: "Cancelada en AlfaField; en Odoo sigue abierta (no se envió)", pill: true };
  }
  if (_odooCancel) return { kind: "error", label: "Odoo ✗", title: "Cancelada en Odoo pero abierta en AlfaField", pill: true };
  const close = String(it?.close_event_status || "").toLowerCase();
  const sync = String(it?.odoo_sync_status || "").toLowerCase();
  const ws = String(it?.odoo_ws_event_status || "").toLowerCase();
  const msg = (it?.close_event_message || "").trim();

  if (close === "completado" || sync === "sincronizada") {
    return { kind: "ok", label: "Odoo OK", title: msg || "Cierre enviado a Odoo correctamente", pill: true };
  }
  if (["error_negocio", "error_validacion"].includes(close)) {
    return { kind: "error", label: "Odoo ✗", title: msg || `Cierre rechazado (${close})`, pill: true };
  }
  if (close === "pendiente_sync" || sync === "pendiente_sync") {
    return { kind: "pending", label: "Odoo…", title: msg || "Cierre pendiente de sincronizar con Odoo", pill: true };
  }
  // Fiscalización: todavía no cerró — mostrar estado de aprovisionamiento V2 si existe
  if (ws === "sync_ok") {
    return { kind: "ok", label: "Prov OK", title: "Aprovisionamiento enviado a Odoo" };
  }
  if (["error", "error_negocio", "failed", "fallido"].includes(ws)) {
    return { kind: "error", label: "Prov ✗", title: "Error al enviar aprovisionamiento a Odoo" };
  }
  if (ws === "pendiente_sync" || ws === "pending") {
    return { kind: "pending", label: "Prov…", title: "Aprovisionamiento pendiente de envío a Odoo" };
  }
  // Realizadas sin rastro de envío → alerta
  const st = String(it?.internal_status_es || it?.internal_status || "").toLowerCase();
  if (["completada", "completed", "finalizada", "solucionada"].includes(st) || close || sync) {
    if (!close && !sync) {
      return { kind: "warn", label: "Sin Odoo", title: "Cerrada en AlfaField sin registro de envío a Odoo", pill: true };
    }
  }
  // v101.162.0 · Abiertas sin novedades: sincronizada con Odoo
  return { kind: "ok", label: "Odoo ✓", title: `Sincronizada con Odoo${it?.odoo_stage_name ? ` · etapa Odoo: ${it.odoo_stage_name}` : ""}` };
}

function OdooSyncBadge({ it, laneKey }) {   // v101.162.0 · en TODAS las columnas
  const st = getOdooSyncState(it);
  if (!st) return null;
  // v101.190.0 · Solo el envío de CIERRE o CANCELACIÓN a Odoo va como etiqueta verde/roja. Lo demás (OT abierta
  //              sincronizada, aprovisionamiento) es un ícono pequeño como los de CPE / equipo, con el detalle al pasar
  //              el mouse; en rojo solo si el aprovisionamiento falló y en ámbar si está pendiente.
  if (!st.pill) {
    const tone = st.kind === "error" ? "text-red-500" : st.kind === "pending" ? "text-amber-500" : "text-slate-400";
    return (
      <span className={`inline-flex items-center cursor-help ${tone}`} title={`${st.label} · ${st.title}`}
        data-testid={`odoo-sync-${it.odoo_task_id}`} onClick={(e) => e.stopPropagation()}>
        <CloudDownload size={10} strokeWidth={2.5} />
      </span>
    );
  }
  const colors = {
    ok: { bg: "#D1FAE5", fg: "#065F46", Icon: CheckCircle2 },
    error: { bg: "#FEE2E2", fg: "#991B1B", Icon: XCircle },
    pending: { bg: "#FEF3C7", fg: "#92400E", Icon: CloudDownload },
    warn: { bg: "#FFEDD5", fg: "#9A3412", Icon: AlertTriangle },
  }[st.kind] || { bg: "#F1F5F9", fg: "#64748B", Icon: AlertCircle };
  const Icon = colors.Icon;
  return (
    <span
      className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[8px] font-black"
      style={{ background: colors.bg, color: colors.fg }}
      title={st.title}
      data-testid={`odoo-sync-${it.odoo_task_id}`}
      onClick={(e) => e.stopPropagation()}
    >
      <Icon size={9} strokeWidth={2.5} />
      {st.label}
    </span>
  );
}

// v101.13.0 · Carriles donde se puede «Actualizar desde Odoo» una OT
const ODOO_REFRESH_LANES = ["nuevas", "infraestructura", "revision", "asignadas", "fiscalizacion"];   // v101.272.0 · + Fiscalización
// v101.47.0 · En Ejecución: solo si el estado es En sitio (no en ruta / en progreso)
const ODOO_REFRESH_ON_SITE = new Set(["on_site", "en_sitio"]);
const canRefreshFromOdoo = (laneKey, it) => {
  if (ODOO_REFRESH_LANES.includes(laneKey)) return true;
  if (laneKey === "en_ejecucion") {
    return ODOO_REFRESH_ON_SITE.has(String(it?.internal_status || "").toLowerCase());
  }
  return false;
};

function SmartKanbanLanes({ lanes, cols, pageSize, cardLabelMode, onOpen, onColPageChange, onActionDone }) {
  const labelMode = cardLabelMode || KANBAN_LABEL_MODES.client;
  const [suggestState, setSuggestState] = useState({});  // { [ot_id]: { loading, suggestions, assigning } }
  const [odooRefreshing, setOdooRefreshing] = useState({}); // { [ot_id]: true }
  const refreshFromOdoo = async (it) => {
    const id = it.odoo_task_id;
    if (!id || odooRefreshing[id]) return;
    setOdooRefreshing((p) => ({ ...p, [id]: true }));
    try {
      const { data } = await api.post(`/v1/ot_smart/${id}/refresh-from-odoo`);
      const ch = data?.changed || [];
      const extra = (data?.notes || []).join(" · ");
      if (data?.closed_in_odoo) toast.success(`OT ${it.afc_code || id}: en Odoo está ${data.stage_name || "cerrada"}; sale de los carriles`);
      else if (ch.length) toast.success(`OT ${it.afc_code || id} actualizada desde Odoo · ${ch.join(", ")}${extra ? " · " + extra : ""}`);
      else toast.info(`OT ${it.afc_code || id}: sin cambios en Odoo${extra ? " · " + extra : ""}`);
      onActionDone && onActionDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar desde Odoo");
    } finally {
      setOdooRefreshing((p) => { const n = { ...p }; delete n[id]; return n; });
    }
  };

  // v101.272.0 · «Actualizar desde Odoo» todo el carril Fiscalización (en segundo plano, con avance)
  const [fiscBulk, setFiscBulk] = useState(null);
  const pollFiscBulk = async () => {
    try {
      const { data } = await api.get("/v1/ot_smart/lanes/fiscalizacion/refresh-from-odoo");
      setFiscBulk(data);
      if (data?.running) { setTimeout(pollFiscBulk, 3000); return; }
      const cerr = (data?.cerradas_en_odoo || []).length, errs = (data?.errores || []).length;
      toast.success(`Fiscalización actualizada desde Odoo: ${data?.hechas || 0} OTs · ${data?.con_cambios || 0} con cambios · ${data?.sin_cambios || 0} sin cambios`
        + (cerr ? ` · ${cerr} ya cerradas/canceladas en Odoo` : "") + (errs ? ` · ${errs} con error` : ""), { duration: 9000 });
      if (errs) toast.error(`Con error: ${(data.errores || []).slice(0, 5).map((e) => `${e.ot} (${e.detalle})`).join(" · ")}`, { duration: 15000 });
      onActionDone && onActionDone();
    } catch (e) { setFiscBulk(null); }
  };
  const refreshFiscBulk = async () => {
    if (fiscBulk?.running) return;
    if (!window.confirm("¿Actualizar desde Odoo TODAS las OTs del carril Fiscalización?\n\nSe releen de Odoo los datos de la tarea, el cliente, la dirección, la actividad y la red (si la OT no está aprovisionada). No cambia el estado de las OTs ni los materiales, fotos o formulario del técnico.")) return;
    try {
      const { data } = await api.post("/v1/ot_smart/lanes/fiscalizacion/refresh-from-odoo", {});
      setFiscBulk(data);
      if (!data?.total) { toast.info("No hay OTs en Fiscalización."); return; }
      toast.info(data?.ya_en_curso ? "Ya hay una actualización en curso." : `Actualizando ${data.total} OTs desde Odoo…`);
      setTimeout(pollFiscBulk, 3000);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo iniciar la actualización"); }
  };

  const loadSuggestions = async (otId) => {
    setSuggestState(p => ({ ...p, [otId]: { loading: true, suggestions: null, assigning: null } }));
    try {
      const r = await api.get(`/a2/suggest-schedule/${otId}`);
      setSuggestState(p => ({ ...p, [otId]: { loading: false, suggestions: r.data?.suggestions || [], assigning: null } }));
    } catch {
      toast.error("No se pudieron cargar sugerencias");
      setSuggestState(p => ({ ...p, [otId]: { loading: false, suggestions: [], assigning: null } }));
    }
  };

  const assignSuggestion = async (otId, s) => {
    const key = `${s.technician_id}-${s.date}-${s.time_start}`;
    setSuggestState(p => ({ ...p, [otId]: { ...p[otId], assigning: key } }));
    try {
      await api.post(`/v1/master-ot/${otId}/force-schedule`, {
        technician_email: s.technician_email,
        scheduled_at: s.exec_start,
      });
      toast.success(`Asignado a ${s.technician_name} el ${s.date} ${s.time_start}`);
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error("Error: " + (e.response?.data?.detail || e.message));
      setSuggestState(p => ({ ...p, [otId]: { ...p[otId], assigning: null } }));
    }
  };
  const SLA_ICONS = {
    ok: { Icon: CheckCircle2, color: "#059669" },
    warning: { Icon: AlertTriangle, color: "#D97706" },
    breached: { Icon: XCircle, color: "#DC2626" },
    none: { Icon: Clock, color: "#94A3B8" },
  };
  const getSlaState = (it) => {
    if (!it.sla_due_at) return "none";
    const due = new Date(it.sla_due_at);
    const diff = due - new Date();
    if (diff < 0) return "breached";
    if (diff < 2 * 3600 * 1000) return "warning";
    return "ok";
  };

  if (!lanes || lanes.length === 0) {
    return (
      <div className="p-8 text-center text-sm text-gray-500 italic" data-testid="smart-kanban-lanes-empty">
        Cargando carriles…
      </div>
    );
  }

  return (
    <div className="px-3 py-4 overflow-x-auto ot-smart-kanban-scroll" data-testid="smart-kanban-lanes">
      <div className="flex gap-3 min-w-max">
        {lanes.map((lane) => {
          const key = lane.key;
          const state = { items: [], total: lane.count || 0, page: 0, loading: false, ...(cols[key] || {}) };
          const total = state.total || lane.count || 0;
          const totalPages = Math.max(1, Math.ceil(total / pageSize));
          const currentPage = state.page || 0;

          return (
            <div
              key={key}
              className="flex flex-col rounded-2xl overflow-hidden flex-shrink-0"
              style={{
                width: 280,
                background: "#F8FAFC",
                border: `1.5px solid ${lane.color}25`,
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              }}
              data-testid={`kanban-col-${key}`}
            >
              {/* Column header */}
              <div
                className="flex items-center gap-2.5 px-3 py-2.5"
                style={{ background: `${lane.color}08`, borderBottom: `2px solid ${lane.color}30` }}
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: lane.color }}
                >
                  <span className="text-white text-xs font-black">{total}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-bold truncate" style={{ color: "#0A3747" }}>
                    {lane.name}
                  </div>
                  {total > pageSize && (
                    <div className="text-[9px] font-mono text-gray-400">
                      Pag {currentPage + 1}/{totalPages}
                    </div>
                  )}
                </div>
                {key === "fiscalizacion" && (
                  <button
                    onClick={refreshFiscBulk}
                    disabled={!!fiscBulk?.running}
                    title="Actualizar desde Odoo todas las OTs de Fiscalización"
                    className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold border"
                    style={{ color: lane.color, borderColor: `${lane.color}55`, background: "#FFF" }}
                    data-testid="fisc-refresh-odoo"
                  >
                    {fiscBulk?.running ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                    {fiscBulk?.running ? `${fiscBulk.hechas || 0}/${fiscBulk.total || 0}` : "Odoo"}
                  </button>
                )}
              </div>

              {/* Cards scroll area */}
              <div className="flex-1 overflow-y-auto p-2 space-y-2" style={{ maxHeight: 540, minHeight: 100 }}>
                {state.loading && state.items.length === 0 ? (
                  <div className="flex justify-center py-10">
                    <Loader2 size={18} className="animate-spin" style={{ color: lane.color }} />
                  </div>
                ) : state.items.length === 0 ? (
                  <div className="text-center text-[10px] text-gray-400 italic py-10 font-mono">Sin OTs</div>
                ) : (
                  state.items.map((it, idx) => {
                    const sla = getSlaState(it);
                    const sm = SLA_ICONS[sla];
                    const integrity = getIntegrity(it);
                    const hasAlert = !integrity.complete;
                    const createdStr = fmtEcDate(it.odoo_created_at);
                    const scheduledStr = fmtEcDate(it.scheduled_at);
                    // v101.15.0 · Edad desde odoo_created_at
                    const ageDays = daysSinceCreated(it.odoo_created_at);
                    const ageStyle = ageDays == null ? null : ageBadgeStyle(ageDays);
                    return (
                      <div
                        key={it.odoo_task_id || idx}
                        onClick={(e) => onOpen(it)}
                        className={`relative bg-white rounded-xl p-2.5 cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 ${hasAlert ? "border-l-[3px] border-l-red-400 border border-red-200" : "border border-gray-100"}`}
                        data-testid={`kanban-card-${it.odoo_task_id}`}
                      >
                        {/* Row 1: Code + edad + SLA badge + Pencil */}
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[11px] font-mono font-bold text-[#0A3747] flex items-center gap-1 flex-wrap">
                            {it.afc_code || it.task_number || `OT-${it.odoo_task_id}`}
                            {/* v101.15.0 · Tag días desde creación (colores por antigüedad) */}
                            {ageStyle && (
                              <span
                                className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[8px] font-black tracking-wide"
                                style={{ background: ageStyle.bg, color: ageStyle.fg }}
                                title={`Creada hace ${ageDays} día${ageDays === 1 ? "" : "s"} (${createdStr || "—"})`}
                                data-testid={`kanban-age-${it.odoo_task_id}`}
                              >
                                {ageStyle.label}
                              </span>
                            )}
                            {/* v97.0.0 · Bandera roja: OT sin tipo de actividad de Odoo */}
                            {!it.activity_type_code && !it.activity_type && (
                              <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-red-600 text-white text-[8px] font-bold"
                                title="Sin tipo de actividad en Odoo" data-testid={`flag-no-activity-${it.odoo_task_id}`}>
                                ⚑ SIN ACTIVIDAD
                              </span>
                            )}
                          </span>
                          <div className="flex items-center gap-1">
                            {canRefreshFromOdoo(key, it) && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); refreshFromOdoo(it); }}
                                disabled={!!odooRefreshing[it.odoo_task_id]}
                                title="Actualizar esta OT desde Odoo"
                                className="w-6 h-6 inline-flex items-center justify-center rounded-md border border-gray-200 text-[#0A3747] hover:bg-gray-100 disabled:opacity-50"
                                data-testid={`btn-refresh-odoo-${it.odoo_task_id}`}
                              >
                                <RefreshCw size={11} className={odooRefreshing[it.odoo_task_id] ? "animate-spin" : ""} />
                              </button>
                            )}
                            <div onClick={(e) => e.stopPropagation()}>
                              <OTActionMenu ot={it} techs={[]} onUpdated={onActionDone} className="inline-block" />
                            </div>
                            <span
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-bold"
                              style={{ background: `${sm.color}12`, color: sm.color }}
                            >
                              <sm.Icon size={8} />
                              {sla === "ok" ? "OK" : sla === "warning" ? "PRONTO" : sla === "none" ? "\u2014" : "VENCIDO"}
                            </span>
                          </div>
                        </div>
                        {/* Row 2: v101.45.1 · Cliente / Título / Ambos (global, todos los carriles) */}
                        <KanbanCardTitleRow it={it} mode={labelMode} />
                        {/* Row 3: Solution type */}
                        <div className="text-[9px] text-gray-500 truncate mt-0.5">
                          {it.solution_type_name || it.plan_name || it.activity_type_code || "Sin tipo"}
                        </div>
                        {/* Row 4: Dates (Creada / Agendada) */}
                        <div className="flex items-center gap-2 mt-1.5 text-[8px] text-gray-400">
                          {createdStr && (
                            <span className="flex items-center gap-0.5" title="Fecha de creación (UTC-5)">
                              <Clock size={8} />
                              <span>{createdStr}</span>
                            </span>
                          )}
                          {scheduledStr && (
                            <span className="flex items-center gap-0.5 text-blue-500 font-semibold" title="Agendada (UTC-5)">
                              <CalendarClock size={8} />
                              <span>{scheduledStr}</span>
                            </span>
                          )}
                        </div>
                        {/* Row 5: Tech + Integrity + Odoo sync (v101.46.1) */}
                        <div className="flex items-center justify-between mt-1.5 text-[9px] gap-1">
                          <span className="flex items-center gap-1 text-gray-400 min-w-0">
                            <User size={9} />
                            <span className="truncate max-w-[90px]">{it.technician_name || "Sin técnico"}</span>
                          </span>
                          <span className="flex items-center gap-1 flex-shrink-0">
                            <OdooSyncBadge it={it} laneKey={key} />
                            <IntegrityIcons integrity={integrity} />
                          </span>
                        </div>
                        {/* v38.0.0 · Sugerencias IA (solo en revisión) */}
                        {key === "revision" && (() => {
                          const ss = suggestState[it.odoo_task_id] || {};
                          return (
                            <div className="mt-2 border-t border-gray-100 pt-1.5">
                              {!ss.suggestions && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); loadSuggestions(it.odoo_task_id); }}
                                  disabled={ss.loading}
                                  className="w-full text-[10px] font-bold py-1 rounded bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-50"
                                  data-testid={`btn-suggest-${it.odoo_task_id}`}
                                >
                                  {ss.loading ? "Calculando..." : "Sugerir Horarios"}
                                </button>
                              )}
                              {ss.suggestions && ss.suggestions.length > 0 && (
                                <div className="space-y-1.5" data-testid={`suggestions-${it.odoo_task_id}`}>
                                  <div className="text-[8px] font-bold text-cyan-700 uppercase tracking-wider">Sugerencias IA</div>
                                  {ss.suggestions.map((s, i) => {
                                    const sKey = `${s.technician_id}-${s.date}-${s.time_start}`;
                                    return (
                                      <div key={sKey} className="border border-cyan-200 rounded-md p-1.5 bg-cyan-50">
                                        <div className="text-[10px] font-bold text-slate-800 truncate">{s.technician_name}</div>
                                        <div className="text-[9px] text-cyan-800 font-semibold">{s.date} &middot; {s.time_start}-{s.time_end}</div>
                                        <div className="flex items-center gap-1.5 text-[8px] text-gray-400 mt-0.5">
                                          {s.distance_km != null && <span>{s.distance_km}km</span>}
                                          <span>Carga:{s.carga_actual}</span>
                                        </div>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); assignSuggestion(it.odoo_task_id, s); }}
                                          disabled={ss.assigning === sKey}
                                          className="mt-1 w-full text-[9px] font-bold py-0.5 rounded bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50"
                                          data-testid={`btn-assign-${it.odoo_task_id}-${i}`}
                                        >
                                          {ss.assigning === sKey ? "Asignando..." : "Asignar"}
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              {ss.suggestions && ss.suggestions.length === 0 && (
                                <div className="text-[9px] text-amber-600 font-medium bg-amber-50 rounded p-1 text-center">
                                  Sin opciones disponibles
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })
                )}
              </div>

              {/* Pagination footer */}
              {total > pageSize && (
                <div className="flex items-center justify-between px-3 py-1.5 border-t" style={{ borderColor: `${lane.color}15` }}>
                  <button
                    onClick={() => { if (currentPage > 0) onColPageChange(key, currentPage - 1); }}
                    disabled={currentPage === 0 || state.loading}
                    className="p-1 rounded hover:bg-white disabled:opacity-30"
                    data-testid={`kanban-prev-${key}`}
                  >
                    <ChevronLeft size={14} style={{ color: lane.color }} />
                  </button>
                  <span className="text-[10px] font-mono font-bold" style={{ color: lane.color }}>
                    {currentPage + 1}/{totalPages}
                  </span>
                  <button
                    onClick={() => { if (currentPage < totalPages - 1) onColPageChange(key, currentPage + 1); }}
                    disabled={currentPage >= totalPages - 1 || state.loading}
                    className="p-1 rounded hover:bg-white disabled:opacity-30"
                    data-testid={`kanban-next-${key}`}
                  >
                    <ChevronRight size={14} style={{ color: lane.color }} />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// v2.3 · Mapeo SMART status → status de OT manual para colores del CalendarView
function mapSmartStatusToCalStatus(smart) {
  const map = {
    pending: "pendiente_agendamiento",
    assigned: "asignada",
    in_progress: "en_ejecucion",
    on_site: "en_ejecucion",
    completed: "cerrada",
    cancelled: "cancelada",
  };
  return map[smart] || "pendiente_agendamiento";
}
